import { StyledSelect } from "@/components/StyledSelect";
import { useTrans } from "@/translations";

import {
  Control,
  Controller,
  FieldValues,
  Path,
  PathValue,
} from "react-hook-form";
import Select from "react-select";

import ErrorTooltip from "../ErrorTooltip";

type ViewProps = Omit<React.ComponentProps<typeof Select>, "onChange" | "name">;
type CustomProps<T extends FieldValues> = {
  name: string;
  label?: string;
  patternError?: string;
  control?: Control<T>;
  placeholder?: string;
  validationFunction?: (value: string) => boolean | string;
  onSelectChange?: (value: any, name: string) => void;
};

/**
 * Select component with a built in form controller from react-hook-form.
 */
const SelectInput = <T extends FieldValues>(
  props: ViewProps & CustomProps<T>,
) => {
  const {
    id,
    control,
    name,
    options,
    defaultValue,
    isDisabled,
    label,
    required,
    patternError,
    styles,
    placeholder,
    menuPortalTarget,
    validationFunction,
    onSelectChange,
  } = props;

  const { t } = useTrans();

  return (
    <Controller
      name={name as Path<T>}
      control={control}
      defaultValue={defaultValue as PathValue<T, Path<T>>}
      rules={{ required, validate: validationFunction }}
      render={({ field: { onChange, value, name }, fieldState: { error } }) => (
        <div>
          {label && (
            <label
              htmlFor={id}
              className="font-dm-sans text-sm font-medium text-black empty:hidden"
            >
              {label}
              {label && required && " *"}
            </label>
          )}
          <div className="relative flex flex-col">
            <StyledSelect
              required={required}
              defaultValue={defaultValue}
              isDisabled={isDisabled}
              styles={styles}
              options={options}
              placeholder={placeholder ?? t("formLabels:chooseAlternative")}
              value={options?.find((o: any) => o.value === value)}
              onChange={(val) => {
                onChange((val as any).value);
                if (onSelectChange) {
                  onSelectChange(val, name);
                }
              }}
              menuPortalTarget={menuPortalTarget}
              menuPosition="fixed"
              showErrorState={!!error}
              {...props}
            />
            <ErrorTooltip
              label={label}
              patternError={patternError}
              error={error}
              className="mr-4 text-wrap"
            />
          </div>
        </div>
      )}
    />
  );
};

export default SelectInput;
